import { useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";
import { fetchPlan } from "../../store/actions/billingActions";
import { usePlanLimits } from "../../hooks/usePlanLimits";
import { TrialExpiredBanner } from "../TrialExpiredBanner";
import {
  Bell,
  Calendar,
  BarChart2,
  ChevronDown,
  CreditCard,
  Home,
  LifeBuoy,
  LogOut,
  Mail,
  Menu,
  MessageSquare,
  Package,
  Percent,
  Plug,
  Rocket,
  ShoppingCart,
  Store,
  UserCircle,
  Users,
} from "lucide-react";
import { Button } from "../ui/Button";
import { PlanBadge } from "./PlanBadge";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { useToast } from "../../hooks/useToast";

const Shell = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;
`;

const SIDEBAR_WIDTH = 280;

const Sidebar = styled.aside`
  position: fixed;
  left: 0;
  top: 0;
  width: ${SIDEBAR_WIDTH}px;
  height: 100vh;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  padding: 24px 0;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors?.light?.background || theme.colors?.background?.primary || "#fff"};
  z-index: 100;
`;

const SidebarSpacer = styled.div`
  width: ${SIDEBAR_WIDTH}px;
  flex-shrink: 0;
`;

const Brand = styled.div`
  padding: 0 24px 16px;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary.light};
`;

const Nav = styled.nav`
  padding: 0 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const NavGroupLabel = styled.span`
  display: block;
  padding: 8px 16px 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.muted || "#6b7280"};
`;

const NavItem = styled.button`
  appearance: none;
  width: 100%;
  border: none;
  background: ${({ $active }) => ($active ? "rgba(155, 135, 245, 0.10)" : "transparent")};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary.light : theme.colors.text.body)};
  border-radius: ${({ theme }) => theme.radii.base}px;
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  font-weight: 600;
  text-align: left;

  &:hover {
    background: rgba(155, 135, 245, 0.10);
    color: ${({ theme }) => theme.colors.primary.light};
  }
`;

const Main = styled.main`
  flex: 1;
  min-width: 0;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MainScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Header = styled.header`
  height: ${({ theme }) => theme.layout.headerHeight}px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${({ theme }) => theme.layout.pagePaddingX}px;
`;

const Page = styled.div`
  padding: 24px ${({ theme }) => theme.layout.pagePaddingX}px;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserPill = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border-radius: 9999px;
  padding: 6px 10px;
  color: ${({ theme }) => theme.colors.text.body};

  &:hover {
    background: rgba(155, 135, 245, 0.08);
  }
`;

const Avatar = styled.div`
  height: 32px;
  width: 32px;
  border-radius: 9999px;
  background: rgba(155, 135, 245, 0.18);
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary.DEFAULT};
`;

const navGroups = [
  {
    labelKey: "layout.overview",
    items: [
      { nameKey: "layout.dashboard", icon: Home, path: "/dashboard" },
      { nameKey: "layout.getStarted", icon: Rocket, path: "/get-started" },
    ],
  },
  {
    labelKey: "layout.salesAndStores",
    items: [
      { nameKey: "layout.stores", icon: Store, path: "/stores" },
      { nameKey: "layout.customers", icon: UserCircle, path: "/customers" },
      { nameKey: "layout.orders", icon: ShoppingCart, path: "/orders" },
      { nameKey: "layout.products", icon: Package, path: "/products" },
      { nameKey: "layout.coupons", icon: Percent, path: "/coupons" },
      { nameKey: "layout.conversations", icon: MessageSquare, path: "/conversations" },
      { nameKey: "layout.marketing", icon: Mail, path: "/marketing" },
    ],
  },
  {
    labelKey: "layout.teamAndAccount",
    items: [
      { nameKey: "layout.users", icon: Users, path: "/users" },
      { nameKey: "layout.tasks", icon: Calendar, path: "/tasks" },
      { nameKey: "layout.analytics", icon: BarChart2, path: "/analytics" },
      { nameKey: "layout.extensions", icon: Plug, path: "/extensions" },
      { nameKey: "layout.billing", icon: CreditCard, path: "/billing" },
    ],
  },
  {
    labelKey: "layout.help",
    items: [
      { nameKey: "layout.support", icon: LifeBuoy, path: "/support" },
    ],
  },
];

function getInitials(user) {
  if (!user) return "?";
  if (user.name && typeof user.name === "string") {
    const parts = user.name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
  }
  if (user.email && typeof user.email === "string") return user.email.slice(0, 2).toUpperCase();
  return "?";
}

export function AppLayout({ children }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, logout } = useAuth();
  const initials = useMemo(() => getInitials(user), [user]);
  const { trialExpired } = usePlanLimits();

  useEffect(() => {
    dispatch(fetchPlan());
  }, [dispatch]);

  const activePath = useMemo(() => location.pathname, [location.pathname]);

  const isNavActive = (path) => {
    if (path === "/dashboard" || path === "/get-started") return activePath === path;
    if (path === "/support") return activePath === path || activePath.startsWith("/support/");
    if (path === "/products") return activePath === path || activePath.startsWith("/products/");
    if (path === "/orders") return activePath === path || activePath.startsWith("/orders/");
    if (path === "/customers") return activePath === path || activePath.startsWith("/customers/");
    if (path === "/marketing") return activePath === path;
    return activePath === path;
  };

  const handleLogout = () => {
    logout();
    toast({
      title: t("layout.loggedOut"),
      description: t("layout.loggedOutDescription"),
    });
    navigate("/login");
  };

  return (
    <Shell>
      <Sidebar>
        <Brand>{t("common.storeHub")}</Brand>
        <Nav>
          {navGroups.map((group) => (
            <div key={group.labelKey}>
              <NavGroupLabel>{t(group.labelKey)}</NavGroupLabel>
              {group.items.map((item) => {
                if (item.adminOnly && user?.role !== "admin") return null;
                const Icon = item.icon;
                const isActive = isNavActive(item.path);
                return (
                  <NavItem
                    key={item.nameKey}
                    $active={isActive}
                    onClick={() => navigate(item.path)}
                  >
                    <Icon size={20} />
                    <span>{t(item.nameKey)}</span>
                  </NavItem>
                );
              })}
            </div>
          ))}
        </Nav>
      </Sidebar>

      <SidebarSpacer aria-hidden="true" />

      <Main>
        <Header>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Button $variant="ghost" $size="icon" aria-label="Menu">
              <Menu size={22} />
            </Button>
          </div>

          <Right>
            <PlanBadge />
            <LanguageSwitcher />

            <div style={{ position: "relative" }}>
              <Button $variant="ghost" $size="icon" aria-label={t("layout.notifications")}>
                <Bell size={20} />
              </Button>
              <span
                style={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  width: 8,
                  height: 8,
                  borderRadius: 9999,
                  background: "#ef4444",
                }}
              />
            </div>

            <UserPill onClick={handleLogout} aria-label={t("layout.userMenu")}>
              <Avatar>{initials}</Avatar>
              <ChevronDown size={16} />
              <span style={{ position: "absolute", left: "-9999px" }}>
                {t("layout.userMenu")}
              </span>
              <span style={{ display: "none" }}>{t("layout.menu")}</span>
              <span style={{ display: "none" }}>
                <LogOut size={16} />
              </span>
            </UserPill>

            <Button $variant="outline" $size="sm" onClick={handleLogout}>
              <LogOut size={16} />
              {t("layout.logOut")}
            </Button>
          </Right>
        </Header>

        {trialExpired && <TrialExpiredBanner />}
        <MainScroll>
          <Page>{children}</Page>
        </MainScroll>
      </Main>
    </Shell>
  );
}

